/* Night Owl Pyjamas redesign — behaviour for the `no-` sections.
   The basket is our own drawer/page (sections/no-cart-drawer, no-main-cart), re-rendered
   with Shopify's Section Rendering API after every cart change. */
(() => {
  if (window.NightOwl) return;

  const NO_T = window.NO_T || {};
  const noT = (k, fb, vars) => { let t = NO_T[k] || fb; for (const v in (vars || {})) t = t.split('{{ ' + v + ' }}').join(vars[v]); return t; };

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const isCartPage = () => /\/cart\/?$/.test(window.location.pathname);

  /* ---------- Basket ---------- */
  const cartRoots = () => [...document.querySelectorAll('[data-no-cart-root]')];
  const cartSectionIds = () => {
    const ids = new Set();
    document.querySelectorAll('[data-no-cart-drawer]').forEach(d => d.dataset.sectionId && ids.add(d.dataset.sectionId));
    cartRoots().forEach(r => r.dataset.sectionId && ids.add(r.dataset.sectionId));
    return [...ids];
  };

  function renderCart(sections, count) {
    if (sections) {
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-no-cart-root]');
        const host = document.querySelector(`[data-no-cart-drawer][data-section-id="${id}"] [data-no-cart-root], [data-no-cart-root][data-section-id="${id}"]`);
        if (fresh && host) host.innerHTML = fresh.innerHTML;
      });
    }
    if (typeof count === 'number') setCount(count);
  }

  async function cartRequest(path, body) {
    const sections = cartSectionIds();
    cartRoots().forEach(r => r.setAttribute('aria-busy', 'true'));
    try {
      const res = await fetch(`${root()}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: sections.join(','), sections_url: window.location.pathname })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || 'Sorry, something went wrong.');
      let count = json.item_count;
      let secs = json.sections;
      if (path === 'cart/add.js' || typeof count !== 'number') {
        // add.js doesn't return the whole cart; re-fetch so totals/discounts are current.
        const r = await fetch(`${root()}?sections=${sections.join(',')}`);
        secs = sections.length ? await r.json() : null;
        count = (await (await fetch(`${root()}cart.js`)).json()).item_count;
      }
      renderCart(secs, count);
      document.documentElement.dispatchEvent(new CustomEvent('no:cart-updated', { detail: { count } }));
      return json;
    } finally {
      cartRoots().forEach(r => r.removeAttribute('aria-busy'));
    }
  }

  function openCart() {
    if (isCartPage()) return;
    const d = document.querySelector('[data-no-cart-drawer]');
    if (d) setDrawer(d, true); else window.location.href = `${root()}cart`;
  }

  async function addToCart(items, trigger) {
    if (trigger) trigger.setAttribute('aria-busy', 'true');
    try {
      await cartRequest('cart/add.js', { items });
      openCart();
      return true;
    } catch (err) {
      showError(trigger, err.message);
      return false;
    } finally {
      if (trigger) trigger.removeAttribute('aria-busy');
    }
  }

  // Basket interactions (drawer and page share markup).
  document.addEventListener('click', async e => {
    const opener = e.target.closest('[data-no-cart-open]');
    if (opener && !isCartPage() && document.querySelector('[data-no-cart-drawer]')) { e.preventDefault(); openCart(); return; }

    const line = e.target.closest('[data-no-cart-root] .no-line');
    const step = e.target.closest('[data-no-line-step]');
    if (step && line) {
      const input = line.querySelector('[data-no-line-qty]');
      const q = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.noLineStep));
      input.value = q;
      cartRequest('cart/change.js', { id: line.dataset.key, quantity: q }).catch(err => showError(line.querySelector('.no-line__info'), err.message));
      return;
    }
    if (e.target.closest('[data-no-line-remove]') && line) {
      cartRequest('cart/change.js', { id: line.dataset.key, quantity: 0 }).catch(err => showError(line.querySelector('.no-line__info'), err.message));
      return;
    }
    const add = e.target.closest('[data-no-cart-root] [data-no-cart-add]');
    if (add) {
      const card = add.closest('.no-cart__card');
      const sel = card && card.querySelector('[data-no-upsell-variant]');
      const id = Number(sel ? sel.value : add.dataset.noCartAdd);
      add.setAttribute('aria-busy', 'true');
      cartRequest('cart/add.js', { items: [{ id, quantity: 1 }] }).catch(err => showError(card, err.message)).finally(() => add.removeAttribute('aria-busy'));
    }
  });
  document.addEventListener('change', e => {
    const qty = e.target.closest('[data-no-cart-root] [data-no-line-qty]');
    if (qty) {
      const line = qty.closest('.no-line');
      cartRequest('cart/change.js', { id: line.dataset.key, quantity: Math.max(0, parseInt(qty.value, 10) || 0) }).catch(err => showError(line.querySelector('.no-line__info'), err.message));
      return;
    }
    const wrap = e.target.closest('[data-no-cart-root] [data-no-giftwrap]');
    if (wrap) {
      if (wrap.checked) cartRequest('cart/add.js', { items: [{ id: Number(wrap.dataset.noGiftwrap), quantity: 1 }] });
      else if (wrap.dataset.line) cartRequest('cart/change.js', { id: wrap.dataset.line, quantity: 0 });
      return;
    }
    const note = e.target.closest('[data-no-cart-note]');
    if (note) fetch(`${root()}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
  });
  // Keep the basket in step with anything else that changes the cart (theme forms, apps, other tabs).
  document.addEventListener('variant:add', () => { cartRequest('cart/update.js', { updates: {} }).then(openCart).catch(() => {}); });
  document.addEventListener('cart:refresh', () => { cartRequest('cart/update.js', { updates: {} }).catch(() => {}); });
  window.addEventListener('pageshow', e => { if (e.persisted) cartRequest('cart/update.js', { updates: {} }).catch(() => {}); });

  function showError(near, message) {
    const host = near && (near.closest('[data-no-errors]') || near.parentElement);
    if (!host) { alertless(message); return; }
    let el = host.querySelector(':scope > .no-msg-error');
    if (!el) { el = document.createElement('p'); el.className = 'no-msg-error'; el.setAttribute('role', 'alert'); host.appendChild(el); }
    el.textContent = message;
    setTimeout(() => el.remove(), 6000);
  }
  function alertless(message) { console.warn('[Night Owl]', message); }

  /* ---------- Modal ---------- */
  let modal, lastFocus;
  function openModal(content, extraClass) {
    closeModal();
    lastFocus = document.activeElement;
    modal = document.createElement('div');
    modal.className = 'no-modal' + (extraClass ? ' ' + extraClass : '');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML = '<div class="no-modal__panel"><button type="button" class="no-modal__close" aria-label="Close"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></button></div>';
    const panel = modal.firstElementChild;
    panel.append(content);
    modal.addEventListener('click', e => { if (e.target === modal || e.target.closest('.no-modal__close')) closeModal(); });
    document.body.appendChild(modal);
    document.documentElement.style.overflow = 'hidden';
    requestAnimationFrame(() => { modal.setAttribute('open', ''); (panel.querySelector('button:not(.no-modal__close), a, video') || panel.querySelector('.no-modal__close')).focus({ preventScroll: true }); });
  }
  function closeModal() {
    if (!modal) return;
    modal.querySelectorAll('video').forEach(v => v.pause());
    const m = modal; modal = null;
    m.removeAttribute('open');
    document.documentElement.style.overflow = '';
    setTimeout(() => m.remove(), 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  /* ---------- Variant chooser ---------- */
  function openChooser(data, trigger) {
    const wrap = document.createElement('div');
    wrap.style.display = 'contents';
    const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    wrap.innerHTML = `
      <div class="no-chooser__head">${data.image ? `<img src="${esc(data.image)}" alt="">` : '<span></span>'}
        <div><span class="no-eyebrow">Choose an option</span><div class="no-chooser__title">${esc(data.title)}</div></div></div>
      <div class="no-chooser__opts" data-no-errors>${data.variants.map(v =>
        `<button type="button" class="no-chooser__opt" data-id="${v.id}" ${v.available ? '' : 'disabled'}>${esc(v.title)}<br><small>${esc(v.price)}</small></button>`).join('')}</div>
      <a class="no-chooser__link" href="${esc(data.url)}">View full details</a>`;
    wrap.addEventListener('click', async e => {
      const b = e.target.closest('.no-chooser__opt');
      if (!b || b.disabled) return;
      if (await addToCart([{ id: Number(b.dataset.id), quantity: 1 }], b)) closeModal();
    });
    openModal(wrap);
  }

  /* ---------- Delegated clicks ---------- */
  document.addEventListener('click', e => {
    const add = e.target.closest('[data-no-add]');
    if (add) { e.preventDefault(); addToCart([{ id: Number(add.dataset.noAdd), quantity: 1 }], add); return; }

    const choose = e.target.closest('[data-no-choose]');
    if (choose) {
      e.preventDefault();
      const scope = choose.closest('.no-card') || document;
      const node = scope.querySelector(`script[data-no-product="${CSS.escape(choose.dataset.noChoose)}"]`);
      if (node) openChooser(JSON.parse(node.textContent), choose);
      return;
    }

    const film = e.target.closest('[data-no-film]');
    if (film) {
      e.preventDefault();
      const tpl = document.getElementById(film.dataset.noFilm);
      if (!tpl) return;
      const frag = tpl.content.cloneNode(true);
      openModal(frag, 'no-filmmodal');
      const v = modal && modal.querySelector('video');
      if (v) v.play().catch(() => {});
      return;
    }

    const bundle = e.target.closest('[data-no-bundle-add]');
    if (bundle) {
      e.preventDefault();
      const form = bundle.closest('[data-no-bundle]');
      const ids = [...form.querySelectorAll('[data-no-bundle-variant]')].map(el => Number(el.value)).filter(Boolean);
      if (ids.length) addToCart(ids.map(id => ({ id, quantity: 1 })), bundle);
    }
  });

  document.addEventListener('change', e => {
    const sel = e.target.closest('[data-no-bundle-variant]');
    if (!sel) return;
    const form = sel.closest('[data-no-bundle]');
    updateBundle(form);
  });

  function updateBundle(form) {
    if (!form) return;
    const pct = Number(form.dataset.percent || 0);
    let total = 0, last = 0, ok = true;
    form.querySelectorAll('[data-no-bundle-variant]').forEach(el => {
      const opt = el.tagName === 'SELECT' ? el.selectedOptions[0] : el;
      const cents = Number(opt.dataset.price || 0);
      total += cents; last = cents;
      const row = el.closest('.no-bundle__item');
      const priceEl = row && row.querySelector('[data-no-bundle-price]');
      if (priceEl && opt.dataset.priceFormatted) priceEl.textContent = opt.dataset.priceFormatted;
      if (opt.dataset.available === 'false') ok = false;
    });
    const fmt = c => (window.Shopify && Shopify.formatMoney) ? Shopify.formatMoney(c) : '£' + (c / 100).toFixed(2);
    const now = total - Math.round(last * pct / 100);
    const set = (sel, v) => { const n = form.querySelector(sel); if (n) n.textContent = v; };
    set('[data-no-bundle-now]', fmt(now));
    set('[data-no-bundle-was]', fmt(total));
    set('[data-no-bundle-save]', noT('you_save', 'You save {{ amount }}', { amount: fmt(total - now) }));
    set('[data-no-bundle-klarna]', fmt(Math.round(now / 3)));
    const btn = form.querySelector('[data-no-bundle-add]');
    if (btn) { btn.disabled = !ok; btn.textContent = ok ? btn.dataset.label : noT('sold_out', 'Sold out'); }
  }

  /* ---------- Header ---------- */
  function initHeader(scope) {
    scope.querySelectorAll('[data-no-header]:not([data-ready])').forEach(wrap => {
      wrap.setAttribute('data-ready', '');

      // Announcement rotation
      const msgs = [...wrap.querySelectorAll('.no-announce__msg')];
      if (msgs.length > 1) {
        let i = 0;
        const speed = Number(wrap.dataset.interval || 5) * 1000;
        setInterval(() => {
          if (document.hidden) return;
          msgs[i].classList.remove('is-active'); msgs[i].setAttribute('aria-hidden', 'true');
          i = (i + 1) % msgs.length;
          msgs[i].classList.add('is-active'); msgs[i].removeAttribute('aria-hidden');
        }, speed);
      }

      // Transparent-over-hero → solid on scroll
      if (wrap.classList.contains('no-header-wrap--clear')) {
        // Sits below any app banner (e.g. BOGOS) at load, then pins to the top on scroll.
        const startTop = () => wrap.classList.contains('is-pinned') ? wrap._noTop || 0 : (wrap._noTop = wrap.getBoundingClientRect().top + window.scrollY);
        const onScroll = () => {
          const top = startTop();
          wrap.classList.toggle('is-pinned', window.scrollY > top);
          wrap.classList.toggle('is-solid', window.scrollY > top + 60 || wrap.classList.contains('is-menu-open'));
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }

      // Mega menus
      const triggers = [...wrap.querySelectorAll('[data-no-mega-trigger]')];
      let closeTimer;
      const setMega = (id, open) => {
        wrap.querySelectorAll('.no-mega').forEach(m => {
          const isIt = m.id === id && open;
          m.classList.toggle('is-open', isIt);
        });
        triggers.forEach(t => t.setAttribute('aria-expanded', String(t.dataset.noMegaTrigger === id && open)));
        wrap.classList.toggle('is-menu-open', !!open);
        if (wrap.classList.contains('no-header-wrap--clear')) wrap.classList.toggle('is-solid', open || window.scrollY > (wrap._noTop || 0) + 60);
      };
      triggers.forEach(t => {
        const id = t.dataset.noMegaTrigger;
        t.addEventListener('mouseenter', () => { clearTimeout(closeTimer); setMega(id, true); });
        t.addEventListener('click', e => { e.preventDefault(); setMega(id, t.getAttribute('aria-expanded') !== 'true'); });
      });
      wrap.querySelectorAll('.no-header__nav > :not([data-no-mega-trigger]), .no-header__logo').forEach(el => el.addEventListener('mouseenter', () => setMega(null, false)));
      wrap.addEventListener('mouseleave', () => { closeTimer = setTimeout(() => setMega(null, false), 120); });
      wrap.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      wrap.addEventListener('keydown', e => { if (e.key === 'Escape') setMega(null, false); });
      wrap.addEventListener('focusout', e => { if (!wrap.contains(e.relatedTarget)) setMega(null, false); });

      // Mobile drawer
      const drawer = wrap.querySelector('.no-drawer');
      if (drawer) {
        const open = v => {
          drawer.classList.toggle('is-open', v);
          drawer.setAttribute('aria-hidden', String(!v));
          document.documentElement.style.overflow = v ? 'hidden' : '';
          wrap.classList.toggle('is-menu-open', v);
          if (wrap.classList.contains('no-header-wrap--clear')) wrap.classList.toggle('is-solid', v || window.scrollY > (wrap._noTop || 0) + 60);
          if (v) drawer.querySelector('.no-drawer__panel button, .no-drawer__panel a').focus({ preventScroll: true });
        };
        wrap.querySelectorAll('[data-no-menu-open]').forEach(b => b.addEventListener('click', () => open(true)));
        drawer.querySelectorAll('[data-no-menu-close]').forEach(b => b.addEventListener('click', () => open(false)));
        drawer.addEventListener('keydown', e => { if (e.key === 'Escape') open(false); });
      }
    });
  }

  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeModal(); });

  // Header bag count + drawer title count.
  function setCount(n) {
    document.querySelectorAll('.no-header-wrap cart-count, [data-no-cart-count]').forEach(el => {
      el.textContent = n;
      if (el.tagName === 'CART-COUNT') { el.classList.toggle('opacity-0', n === 0); el.style.opacity = n === 0 ? '0' : ''; }
    });
  }

  /* ---------- Product page ---------- */
  function money(cents, format) {
    const f = format || '£{{amount}}';
    const val = (cents / 100).toFixed(2);
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) =>
      k === 'amount_no_decimals' ? Math.round(cents / 100).toString()
      : k === 'amount_with_comma_separator' ? val.replace('.', ',')
      : val.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  }

  function initPdp(scope) {
    scope.querySelectorAll('[data-no-pdp]:not([data-ready])').forEach(root => {
      root.setAttribute('data-ready', '');
      const json = root.querySelector('[data-no-pdp-json]');
      if (!json) return;
      const data = JSON.parse(json.textContent);
      const fmt = c => money(c, data.moneyFormat);
      const form = document.getElementById(root.dataset.formId);
      const idInput = form && form.querySelector('[data-no-variant-id]');
      const qtyInput = form && form.querySelector('[data-no-qty-input]');
      const submit = form && form.querySelector('[data-no-submit]');
      const fieldsets = [...root.querySelectorAll('[data-no-option]')];
      const offers = root.querySelector('[data-no-offers]');
      const sizeSet = root.querySelector('[data-no-size]');
      const sticky = root.querySelector('[data-no-stickybuy]');
      const gallery = root.querySelector('[data-no-gallery]');

      const selected = () => fieldsets.map(fs => { const c = fs.querySelector('input:checked'); return c ? c.value : null; });
      const findVariant = opts => opts.includes(null) ? null : data.variants.find(v => v.options.every((o, i) => o === opts[i]));
      const qty = () => Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
      const offerMode = () => { const c = offers && offers.querySelector('input[name^="no-offer-"]:checked'); return c ? c.value : 'single'; };
      const offerVariant = () => offers && offers.querySelector('[data-no-offer-variant]');
      const offerPrice = () => { const el = offerVariant(); if (!el) return 0; const o = el.tagName === 'SELECT' ? el.selectedOptions[0] : el; return Number(o.dataset.price || 0); };

      function refresh(fromUser) {
        const opts = selected();
        // Use the first variant matching the options picked so far for price display.
        const exact = findVariant(opts);
        const partial = exact || data.variants.find(v => opts.every((o, i) => o === null || v.options[i] === o)) || data.variants[0];
        const v = exact || (fieldsets.length ? null : data.variants[0]);
        const shown = v || partial;

        // Availability markers for each option value given the other picks.
        fieldsets.forEach((fs, i) => {
          fs.querySelectorAll('input').forEach(inp => {
            const ok = data.variants.some(x => x.available && x.options.every((o, j) => j === i ? o === inp.value : (opts[j] === null || o === opts[j])));
            inp.closest('label').classList.toggle('is-unavailable', !ok);
          });
          const lbl = fs.querySelector('[data-no-option-label]');
          if (lbl) lbl.textContent = opts[i] || '';
        });

        const set = (sel, txt) => root.querySelectorAll(sel).forEach(n => { n.textContent = txt; });
        const price = shown.price;
        root.querySelectorAll('[data-no-price]').forEach(n => { n.textContent = fmt(price); n.classList.toggle('is-sale', shown.compare > price); });
        root.querySelectorAll('[data-no-compare]').forEach(n => { n.textContent = fmt(shown.compare); n.hidden = !(shown.compare > price); });
        set('[data-no-klarna]', fmt(Math.round(price / 3)));
        set('[data-no-offer-single]', fmt(price));

        let unit = price;
        if (offers) {
          const pct = Number(offers.dataset.bundlePercent || 0);
          const extra = offerPrice();
          const saving = Math.round(extra * pct / 100);
          const both = price + extra;
          const now = both - saving;
          set('[data-no-offer-now]', fmt(now));
          set('[data-no-offer-was]', saving > 0 ? fmt(both) : '');
          set('[data-no-offer-save]', saving > 0 ? `Save ${fmt(saving)} · ${Math.round(saving / both * 100)}% off the set` : '');
          set('[data-no-b2g1-now]', fmt(price * 2));
          set('[data-no-b2g1-was]', fmt(price * 3));
          const mode = offerMode();
          if (mode === 'bundle') unit = now;
          if (mode === 'b2g1') unit = price * 2;
        }
        const total = unit * qty();

        if (idInput && v) idInput.value = v.id;
        if (submit) {
          submit.disabled = !!v && !v.available;
          submit.textContent = v && !v.available ? noT('sold_out', 'Sold out') : `${noT('add_to_basket', 'Add to basket')} · ${fmt(total)}`;
        }

        const ship = root.querySelector('[data-no-ship]');
        if (ship) {
          const t = Number(ship.dataset.threshold || 0);
          ship.querySelector('[data-no-ship-msg]').textContent = total >= t ? 'You’ve unlocked free UK delivery' : `Add ${fmt(t - total)} more for free UK delivery`;
        }

        if (sticky) {
          set('[data-no-sticky-price]', fmt(total));
          set('[data-no-sticky-variant]', v ? v.title : (sizeSet ? noT('choose_size', 'Choose a size') : ''));
        }

        if (fromUser && v) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          history.replaceState(null, '', url.toString());
        }
        // Only show photos for the chosen colour (photos with no colour in their alt text are shared).
        if (gallery && gallery.dataset.colourIndex !== undefined) {
          const ci = Number(gallery.dataset.colourIndex);
          const pick = ci >= 0 ? (opts[ci] || shown.options[ci]) : null;
          if (pick) {
            const key = '|' + pick.toLowerCase().replace(/-long$/, '').replace(/-/g, ' ') + '|';
            const items = [...gallery.querySelectorAll('.no-gallery__item')];
            items.forEach((item, i) => { if (!item.dataset.order) item.dataset.order = i; if (item.dataset.colours) item.hidden = !item.dataset.colours.includes(key); });
            // Keep the original order for visible photos and park hidden ones at the end so the grid pattern stays intact.
            items.sort((a, b) => (a.hidden - b.hidden) || (a.dataset.order - b.dataset.order)).forEach(item => gallery.appendChild(item));
          }
        }
        // Lead the gallery with the chosen colour's photo (also on first load).
        {
          if (shown.media && gallery) {
            const item = gallery.querySelector(`[data-media-id="${shown.media}"]`);
            if (item && item !== gallery.firstElementChild) {
              gallery.prepend(item);
              if (window.innerWidth < 1000) gallery.scrollTo({ left: 0, behavior: 'smooth' });
            }
          }
        }
        if (fromUser && sizeSet) {
          const msg = sizeSet.querySelector('[data-no-size-msg]');
          if (msg && sizeSet.querySelector('input:checked')) { msg.textContent = msg.dataset.default; msg.classList.remove('is-error'); }
        }
      }

      root.addEventListener('change', e => {
        if (e.target.closest('[data-no-option], [data-no-offers]') || e.target === qtyInput) refresh(true);
      });
      root.addEventListener('click', e => {
        const pick = e.target.closest('[data-no-pick-offer]');
        if (pick && offers) {
          const r = offers.querySelector(`input[name^="no-offer-"][value="${pick.dataset.noPickOffer}"]`);
          if (r) { e.preventDefault(); r.checked = true; refresh(true); r.closest('.no-offer').scrollIntoView({ behavior: 'smooth', block: 'center' }); }
        }
        const step = e.target.closest('[data-no-qty]');
        if (step && qtyInput) { qtyInput.value = Math.max(1, qty() + Number(step.dataset.noQty)); refresh(false); }
        if (e.target.closest('[data-no-sticky-buy]') && form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
      if (qtyInput) qtyInput.addEventListener('input', () => refresh(false));

      if (form) form.addEventListener('submit', async e => {
        e.preventDefault();
        const v = findVariant(selected()) || (fieldsets.length ? null : data.variants[0]);
        if (!v) {
          const missing = fieldsets.find(fs => !fs.querySelector('input:checked'));
          if (missing) {
            const msg = missing.querySelector('[data-no-size-msg]');
            if (msg) { msg.textContent = noT('choose_size_first', 'Please choose a size first.'); msg.classList.add('is-error'); }
            missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const first = missing.querySelector('input'); if (first) first.focus({ preventScroll: true });
          }
          return;
        }
        const q = qty();
        const counts = new Map([[v.id, q]]);
        const mode = offers ? offerMode() : 'single';
        if (mode === 'bundle') {
          const ov = offerVariant();
          if (ov && ov.value) counts.set(Number(ov.value), (counts.get(Number(ov.value)) || 0) + q);
        }
        if (mode === 'b2g1') {
          const ci = Number(offers.dataset.colourIndex);
          [2, 3].forEach(k => {
            let pick = v;
            const chosen = offers.querySelector(`[data-no-set="${k}"]:checked`);
            if (ci >= 0 && chosen) {
              const want = v.options.slice(); want[ci] = chosen.value;
              pick = data.variants.find(x => x.available && x.options.every((o, i) => o === want[i])) || v;
            }
            counts.set(pick.id, (counts.get(pick.id) || 0) + q);
          });
        }
        await addToCart([...counts].map(([id, quantity]) => ({ id, quantity })), submit);
      });

      if (sticky && form) {
        let shown = null;
        const check = () => {
          const show = form.getBoundingClientRect().bottom < 0;
          if (show === shown) return;
          shown = show;
          sticky.classList.toggle('is-visible', show);
          sticky.setAttribute('aria-hidden', String(!show));
          sticky.querySelector('button').tabIndex = show ? 0 : -1;
        };
        window.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check);
        check();
      }

      // Pin the info column; when it's taller than the screen, pin it by its bottom edge
      // so it scrolls with the page until everything has been seen.
      const info = root.querySelector('.no-pdp__info');
      if (info) {
        const fit = () => { info.style.top = Math.min(140, window.innerHeight - info.offsetHeight - 24) + 'px'; };
        fit(); window.addEventListener('resize', fit);
        root.addEventListener('toggle', fit, true);
      }

      refresh(false);
    });
  }

  /* ---------- Generic drawers (filters), sort, sticky offsets ---------- */
  function setDrawer(drawer, open) {
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (open) { const f = drawer.querySelector('.no-drawer__panel button, .no-drawer__panel input, .no-drawer__panel a'); if (f) f.focus({ preventScroll: true }); }
  }
  document.addEventListener('click', e => {
    const opener = e.target.closest('[data-no-drawer-open]');
    if (opener) { const d = document.getElementById(opener.dataset.noDrawerOpen); if (d) setDrawer(d, true); return; }
    const closer = e.target.closest('[data-no-drawer] [data-no-drawer-close]');
    if (closer) setDrawer(closer.closest('[data-no-drawer]'), false);
  });
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-no-drawer].is-open').forEach(d => setDrawer(d, false));
  });
  document.addEventListener('change', e => {
    const sort = e.target.closest('[data-no-sort]');
    if (!sort) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });
  function syncOffsets() {
    const h = document.querySelector('.no-header-wrap--sticky');
    document.documentElement.style.setProperty('--no-toolbar-top', (h ? h.offsetHeight : 0) + 'px');
  }
  window.addEventListener('resize', syncOffsets);

  /* ---------- Estimated delivery (replaces the delivery-date app) ---------- */
  function initEta(scope) {
    scope.querySelectorAll('[data-no-eta]:not([data-ready])').forEach(el => {
      el.setAttribute('data-ready', '');
      const cutoff = Number(el.dataset.cutoff || 14), extra = Number(el.dataset.dispatch || 1);
      const minD = Number(el.dataset.min || 2), maxD = Number(el.dataset.max || 3);
      const ukParts = d => Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: false, weekday: 'short' }).formatToParts(d).map(p => [p.type, p.value]));
      const isWeekend = d => d.getUTCDay() === 0 || d.getUTCDay() === 6;
      const addWorking = (d, n) => { const x = new Date(d); while (n > 0) { x.setUTCDate(x.getUTCDate() + 1); if (!isWeekend(x)) n--; } return x; };
      const fmt = d => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
      function render() {
        try {
          const p = ukParts(new Date());
          const today = new Date(Date.UTC(+p.year, +p.month - 1, +p.day));
          const mins = (+p.hour % 24) * 60 + +p.minute;
          let dispatch = new Date(today), sameDay = !isWeekend(today) && mins < cutoff * 60;
          if (!sameDay) { dispatch = addWorking(today, isWeekend(today) ? 1 : Math.max(1, extra)); }
          const from = addWorking(dispatch, minD), to = addWorking(dispatch, maxD);
          const orderEl = el.querySelector('[data-no-eta-order]');
          if (sameDay) {
            const left = cutoff * 60 - mins, h = Math.floor(left / 60), m = left % 60;
            orderEl.innerHTML = noT('order_within_html', 'Order within <b>{{ time }}</b> for dispatch today', { time: `${h ? h + 'h ' : ''}${m}m` });
          } else {
            orderEl.innerHTML = noT('dispatched_html', 'Dispatched <b>{{ date }}</b>', { date: fmt(dispatch) });
          }
          el.querySelector('[data-no-eta-arrive]').textContent = noT('arrives', 'Arrives {{ date }}', { date: `${fmt(from)}${maxD > minD ? ' – ' + fmt(to) : ''}` });
        } catch (e) { /* keep fallback text */ }
      }
      render(); setInterval(render, 60000);
    });
  }

  /* ---------- Fade-in on scroll ---------- */
  const REVEAL_AUTO = '.no-head-row, .no-card, .no-tile, .no-post, .no-cardx, .no-review, .no-film, .no-col, .no-trust__row, .no-films__head, .no-faq > .no-h2, .no-acc details, .no-prev__summary, .no-feature-post, .no-filmbanner, .no-h2--lead';
  let revealIO;
  function initReveal(scope) {
    if (!('IntersectionObserver' in window) || (window.Shopify && Shopify.designMode)) {
      scope.querySelectorAll('[data-no-reveal]').forEach(el => el.classList.add('is-in'));
      return;
    }
    revealIO = revealIO || new IntersectionObserver(entries => entries.forEach(en => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); revealIO.unobserve(en.target); }
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    scope.querySelectorAll('[data-no-reveal]:not([data-reveal-ready])').forEach(sec => {
      sec.setAttribute('data-reveal-ready', '');
      let i = 0;
      sec.querySelectorAll(REVEAL_AUTO).forEach(el => {
        if (el.closest('.no-reveal') || el.classList.contains('no-reveal')) return;
        el.classList.add('no-reveal'); el.style.setProperty('--i', String(i++ % 6));
      });
      // Observe in chunks so long grids reveal row by row, not all at once.
      const targets = [...sec.querySelectorAll('.no-grid-products, .no-tiles__grid, .no-jgrid, .no-journal__grid, .no-cards, .no-reviews__grid, .no-films__grid, .no-cols3')];
      if (targets.length) { targets.forEach(t => { t.setAttribute('data-no-reveal', ''); t.setAttribute('data-reveal-ready', ''); revealIO.observe(t); }); }
      revealIO.observe(sec);
    });
  }

  function init(scope = document) {
    initHeader(scope);
    initPdp(scope);
    initEta(scope);
    initReveal(scope);
    syncOffsets();
    scope.querySelectorAll('[data-no-bundle]').forEach(updateBundle);
  }
  // "Read full description" opens the description row and scrolls it into view.
  document.addEventListener('click', e => {
    const link = e.target.closest('[data-no-open-desc]');
    const target = link && document.getElementById('no-full-description');
    if (!target) return;
    e.preventDefault();
    target.open = true;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', e => init(e.target));

  window.NightOwl = { addToCart, openModal, closeModal };
})();
